import React, { useMemo, useState } from "react";
import { Add as AddIcon } from "@mui/icons-material";
import { Box, Button } from "@mui/material";
import { getErrorMessage } from "utils/errorHelpers";
import type { BillingAccountDetailDto, BillingAccountSubscriptionDto } from "client/api-v2";
import { ActionDialog } from "components/ActionDialog/ActionDialog";
import { AlertSeverity, useAlerts } from "components/AlertProvider/AlertProvider";
import { DetailsPageLayout } from "components/DetailsPageLayout/DetailsPageLayout";
import { useEntity } from "components/EntityProvider/EntityProvider";
import { PaperLayout } from "components/PaperLayout/PaperLayout";
import { PolicyAuthorized } from "components/authorization/PolicyAuthorized";
import { RbacPolicies } from "components/authorization/RbacPolicies";
import { LinkOrTransferSubscriptionDialog } from "../components/LinkOrTransferSubscriptionDialog";
import { removeSubscriptionFromBillingAccount } from "../services/billingAccountService";
import { BillingAccountSubscriptionsList } from "./BillingAccountSubscriptionsList";

export function BillingAccountSubscriptions() {
  const { sendNewAlert } = useAlerts();
  const { entity: billingAccount, refresh } = useEntity<BillingAccountDetailDto>();
  const [linkDialog, setLinkDialog] = useState<{ open: boolean; subscription?: BillingAccountSubscriptionDto }>({
    open: false,
  });
  const [subscriptionPendingRemoval, setSubscriptionPendingRemoval] = useState<
    BillingAccountSubscriptionDto | undefined
  >(undefined);

  const currentAccountSubscriptionIds = useMemo(
    () => new Set((billingAccount?.subscriptions ?? []).map((s) => s.id)),
    [billingAccount?.subscriptions]
  );

  if (!billingAccount) {
    return null;
  }

  const accountName = billingAccount.commonName ?? billingAccount.legalName;

  const removeSubscription = async () => {
    const subscription = subscriptionPendingRemoval;
    if (!subscription) {
      return;
    }

    try {
      await removeSubscriptionFromBillingAccount(subscription.id);
      sendNewAlert("Subscription removed", AlertSeverity.Success);
    } catch (error) {
      sendNewAlert(getErrorMessage(error), AlertSeverity.Error);
    } finally {
      // The dialog stays dismissable while the request is in flight, so by now the user may have
      // opened it again for another subscription — close only the prompt this removal started from.
      setSubscriptionPendingRemoval((pending) => (pending === subscription ? undefined : pending));
      // Refetch after a failure too — the likeliest one is the link already being gone,
      // and the grid should show whatever the server now holds either way.
      await refresh?.();
    }
  };

  return (
    <PaperLayout maxWidth={false}>
      <DetailsPageLayout
        pageName="Subscriptions"
        actions={[
          <PolicyAuthorized key="assign-subscription" policies={RbacPolicies.Admin.ReadWrite}>
            <Button
              id="assign-subscription-button"
              color="secondary"
              variant="outlined"
              startIcon={<AddIcon />}
              disabled={billingAccount.isReseller}
              onClick={() => setLinkDialog({ open: true, subscription: undefined })}
            >
              Assign Subscription
            </Button>
          </PolicyAuthorized>,
        ]}
      />
      <BillingAccountSubscriptionsList
        subscriptions={billingAccount.subscriptions}
        isReseller={billingAccount.isReseller}
        onTransfer={(subscription) => setLinkDialog({ open: true, subscription })}
        onRemove={setSubscriptionPendingRemoval}
      />
      <ActionDialog
        title="Remove subscription"
        description={
          subscriptionPendingRemoval && (
            <>
              Remove{" "}
              {subscriptionPendingRemoval.friendlyName && <strong>{subscriptionPendingRemoval.friendlyName} · </strong>}
              <Box component="span" sx={{ fontFamily: "monospace" }}>
                {subscriptionPendingRemoval.serial}
              </Box>{" "}
              from {accountName}?
            </>
          )
        }
        show={!!subscriptionPendingRemoval}
        confirmText="Remove"
        onConfirm={removeSubscription}
        onCancel={() => setSubscriptionPendingRemoval(undefined)}
      />
      <LinkOrTransferSubscriptionDialog
        open={linkDialog.open}
        billingAccount={billingAccount}
        subscription={linkDialog.subscription}
        currentAccountSubscriptionIds={currentAccountSubscriptionIds}
        onCancel={() => setLinkDialog((dialog) => ({ ...dialog, open: false }))}
        onSaved={() => {
          const action = linkDialog.subscription ? "transferred" : "assigned";
          setLinkDialog({ open: false });
          sendNewAlert(`Subscription ${action}`, AlertSeverity.Success);
          void refresh?.();
        }}
      />
    </PaperLayout>
  );
}
